import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useFeature, useViewer, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { GraphQLErrorMessage, Modal, Popover, Separator, Spinner } from '@treams/treams-ui';

import { ConversationDateSelect } from '../create-conversation/ConversationDateSelect';
import { ConversationPartnerSelect } from '../create-conversation/ConversationPartnerSelect';
import { ConversationTemplateSelect } from '../create-conversation/ConversationTemplateSelect';

export default function EvaluationSubmissionRequestModal(props: {
	userID: string;
	returnURL: string;
	momentID?: string;
	momentSettings?: graphql.MomentEvaluation;
}) {
	const { t } = useTranslation();
	const history = useHistory();
	const roles = useViewerRoles();
	const viewer = useViewer();
	const hideDeadline = useFeature(graphql.FeatureName.HideConversationDeadlineConfig);

	const [error, attempt] = useErrorBoundary();

	const [manualPickPartner, setManualPickPartner] = useState<boolean>(false);

	const [evaluationFormID, setEvaluationFormID] = useState<string | undefined>(
		props.momentSettings?.evaluationForm?.id ?? undefined,
	);
	const [userID, setUserID] = useState<string | undefined>(undefined);
	const [subjectType, setSubjectType] = useState<'partner' | 'me'>(
		roles.isManager && !props.momentID ? 'partner' : 'me',
	);

	const deadlineDefault = new Date();
	deadlineDefault.setDate(deadlineDefault.getDate() + 30);

	const [deadline, setDeadline] = useState<Date | undefined>(deadlineDefault);
	const [meetingDuration, setMeetingDuration] = useState<number | undefined>(undefined);
	const [location, setLocation] = useState('');

	const [request, { loading: loadingRequest }] = graphql.useRequestEvaluationSubmissionMutation({});
	const [create, { loading: loadingCreate }] = graphql.useCreateEvaluationSubmissionMutation({});
	const loadingMutation = loadingRequest || loadingCreate;

	const { data: managersData } = graphql.useViewerWithManagersQuery();

	const managers = managersData?.viewer.managers.filter((manager) => !manager.source.isArchived);

	const onCreate = attempt(async () => {
		if (!evaluationFormID || !userID) {
			return;
		}

		const baseInput = {
			userID,
			evaluationFormID,
			deadline: deadline,
			momentID: props.momentID,
			meeting: meetingDuration
				? location === ''
					? { duration: meetingDuration }
					: { duration: meetingDuration, location }
				: undefined,
		};

		if (subjectType === 'me') {
			const response = await request({ variables: { input: baseInput } });

			if (response.data) {
				history.push(
					`${props.returnURL}/${response.data.requestEvaluationSubmission.evaluationSubmission.id}`,
				);
			}

			return;
		}

		const response = await create({ variables: { input: baseInput } });

		if (response.data) {
			history.push(
				`${props.returnURL}/${response.data.createEvaluationSubmission.evaluationSubmission.id}`,
			);
		}
	});

	useEffect(() => {
		if (managers && managers.length === 0) {
			setManualPickPartner(true);
		}
	}, [managers]);

	return (
		<Popover visible center maxHeight="calc(100dvh - 40px)">
			<Modal height="auto">
				<Modal.Header title={t('evaluations.request.modal.title')} />

				<Modal.Contents>
					<GraphQLErrorMessage error={error} />
					{loadingMutation || roles.loading ? (
						<Flex justifyContent="center">
							<Spinner />
						</Flex>
					) : (
						<>
							<Box mb={3}>
								<ConversationPartnerSelect
									moment={!!props.momentID}
									titlePrefix="1."
									value={{ userID, subjectType }}
									onChange={(value) => {
										setUserID(value.userID);
										setSubjectType(value.subjectType);
									}}
								/>
							</Box>

							{viewer && userID && !props.momentSettings?.evaluationForm && (
								<Box mb={3}>
									<Separator my={3} />
									<ConversationTemplateSelect
										titlePrefix="2."
										value={evaluationFormID}
										onChange={setEvaluationFormID}
										userID={subjectType === 'me' ? viewer.id : userID}
									/>
								</Box>
							)}

							{userID && evaluationFormID && !hideDeadline && (
								<Box>
									<Separator my={3} />

									<ConversationDateSelect
										titlePrefix={props.momentSettings?.evaluationForm ? '2.' : '3.'}
										value={{
											deadline,
											meetingDuration,
											location,
										}}
										onChange={({ deadline, meetingDuration, location }) => {
											setDeadline(deadline);
											setMeetingDuration(meetingDuration);
											setLocation(location);
										}}
									/>
								</Box>
							)}
						</>
					)}
				</Modal.Contents>
				<Modal.FormFooter
					saveTitle={t('evaluations.request.modal.submit')}
					onCancel={() => {
						if (manualPickPartner && managers && managers?.length > 0) {
							setManualPickPartner(false);

							return;
						}

						history.push(props.returnURL);
					}}
					onSave={() => onCreate()}
					disabled={!evaluationFormID || !userID}
				/>
			</Modal>
		</Popover>
	);
}

const EvaluationRequestLoading = () => {
	const { t } = useTranslation();

	return (
		<Popover visible center>
			<Modal>
				<Modal.Header title={t('evaluations.request.modal.title')} />
				<Modal.Contents>
					<Flex justifyContent="center">
						<Spinner />
					</Flex>
				</Modal.Contents>
			</Modal>
		</Popover>
	);
};

export const EvaluationSubmissionRequestModalMoment = ({
	userID,
	returnURL,
}: {
	userID: string;
	returnURL: string;
}) => {
	const { params } = useRouteMatch<{ momentID: string }>();

	const { data } = graphql.useMomentForEvaluationQuery({ variables: { id: params.momentID } });

	if (!data) {
		return <EvaluationRequestLoading />;
	}

	return (
		<EvaluationSubmissionRequestModal
			userID={userID}
			returnURL={returnURL}
			momentID={params.momentID}
			momentSettings={
				(data?.momentInstance.momentTemplate.evaluation as graphql.MomentEvaluation) ?? undefined
			}
		/>
	);
};
